<template>
  <section class="page" data-testid="page-affinity">
    <header class="page-header">
      <div><h1>好感度</h1><p>查看用户关系分、等级和可解释事件流水。</p></div>
      <button class="button primary" :disabled="loading" @click="() => load()">{{ loading ? "刷新中..." : "刷新" }}</button>
    </header>

    <div class="metrics-grid">
      <MetricCard label="关系用户" :value="states.length" />
      <MetricCard label="信任/亲近" :value="warmCount" />
      <MetricCard label="冷淡/敌对" :value="coldCount" />
      <MetricCard label="手动锁定" :value="lockedCount" />
      <MetricCard label="证据少" :value="thinEvidenceCount" />
      <MetricCard label="当前匹配" :value="filteredStates.length" />
    </div>
    <p v-if="error" class="error-text">{{ error }}</p>
    <p v-if="actionResult" class="save-result">{{ actionResult }}</p>

    <div class="split">
      <div class="panel">
        <h2>关系状态</h2>
        <div class="toolbar">
          <input v-model="query" placeholder="搜索 QQ、等级、最近事件" />
          <button class="button" :class="{ primary: filter === 'all' }" @click="filter = 'all'">全部</button>
          <button class="button" :class="{ primary: filter === 'warm' }" @click="filter = 'warm'">亲近</button>
          <button class="button" :class="{ primary: filter === 'cold' }" @click="filter = 'cold'">冷淡</button>
          <button class="button" :class="{ primary: filter === 'locked' }" @click="filter = 'locked'">锁定</button>
        </div>
        <DataTable :columns="stateColumns" :rows="filteredStates" row-key="user_id">
          <template #score="{ row }"><strong>{{ formatScore(row.score) }}</strong></template>
          <template #level="{ row }"><StatusBadge :status="levelStatus(row.level)">{{ row.level }}</StatusBadge></template>
          <template #manual_override="{ row }">{{ row.manual_override ? "是" : "否" }}</template>
          <template #last_event_at="{ row }">{{ formatTime(row.last_event_at) }}</template>
          <template #relationship_explain="{ row }">
            <div class="explain-cell">
              <strong>{{ evidenceLabel(row.relationship_explain?.evidence_level) }}</strong>
              <span>{{ explainSummary(row.relationship_explain) }}</span>
            </div>
          </template>
          <template #recent_events="{ row }"><pre>{{ eventSummary(row.recent_events) }}</pre></template>
          <template #actions="{ row }">
            <button class="button small" :disabled="loading" @click="selectUser(row.user_id)">详情</button>
            <button class="button small danger" :disabled="loading" @click="clearUser(row.user_id)">清除</button>
          </template>
        </DataTable>
      </div>

      <div>
        <div class="panel">
          <h2>新增事件</h2>
          <div class="form-grid">
            <FormField label="用户 QQ"><input v-model="eventForm.user_id" /></FormField>
            <FormField label="类型">
              <select v-model="eventForm.type">
                <option value="favor">favor</option>
                <option value="grudge">grudge</option>
              </select>
            </FormField>
            <FormField label="强度"><input v-model.number="eventForm.severity" type="number" min="1" max="5" /></FormField>
          </div>
          <FormField label="原因"><textarea v-model="eventForm.description" /></FormField>
          <button class="button primary" :disabled="loading || !canAddEvent" @click="addEvent">{{ loading ? "处理中..." : "添加事件" }}</button>
        </div>

        <div class="panel">
          <h2>手动调整</h2>
          <div class="form-grid">
            <FormField label="用户 QQ"><input v-model="adjustForm.user_id" /></FormField>
            <FormField label="Delta"><input v-model.number="adjustForm.delta" type="number" step="1" /></FormField>
            <FormField label="锁定自动变化">
              <select v-model="adjustForm.manual_override">
                <option :value="true">是</option>
                <option :value="false">否</option>
              </select>
            </FormField>
          </div>
          <FormField label="原因"><textarea v-model="adjustForm.reason" /></FormField>
          <button class="button primary" :disabled="loading || !canAdjust" @click="adjust">{{ loading ? "处理中..." : "保存调整" }}</button>
        </div>

        <div class="panel">
          <h2>事件详情</h2>
          <div v-if="selectedState" class="toolbar">
            <span>用户 {{ selectedState.user_id }}：{{ formatScore(selectedState.score) }} / {{ selectedState.level }}</span>
            <span>事件 {{ selectedEvents.length }} 条</span>
          </div>
          <div v-if="selectedExplain" class="explain-grid">
            <div><span>证据等级</span><strong>{{ evidenceLabel(selectedExplain.evidence_level) }}</strong></div>
            <div><span>净影响</span><strong>{{ formatScore(selectedExplain.net_delta) }}</strong></div>
            <div><span>正/负事件</span><strong>{{ selectedExplain.favor_count }} / {{ selectedExplain.grudge_count }}</strong></div>
            <div><span>信号</span><strong>{{ signalSummary(selectedExplain.signals) || "-" }}</strong></div>
          </div>
          <DataTable :columns="eventColumns" :rows="selectedEvents" row-key="id">
            <template #delta="{ row }">{{ formatScore(row.delta) }}</template>
            <template #timestamp="{ row }">{{ formatTime(row.timestamp) }}</template>
            <template #description="{ row }"><pre>{{ row.description }}</pre></template>
            <template #actions="{ row }">
              <button class="button small danger" :disabled="loading" @click="deleteEvent(row.user_id, row.id)">删除</button>
            </template>
          </DataTable>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from "vue";
import { api, postJson } from "../api/client";
import { useToastStore } from "../stores/toast";
import DataTable from "../ui/DataTable.vue";
import FormField from "../ui/FormField.vue";
import MetricCard from "../ui/MetricCard.vue";
import StatusBadge from "../ui/StatusBadge.vue";

type AffinityState = {
  user_id: number;
  score: number;
  level: string;
  manual_override: boolean;
  confidence: number;
  last_event_at: number;
  recent_events?: AffinityEvent[];
  relationship_explain?: RelationshipExplain;
};
type AffinityEvent = {
  id: number;
  user_id: number;
  type: string;
  description: string;
  source: string;
  severity: number;
  delta: number;
  timestamp: number;
};
type RelationshipExplain = {
  event_count: number;
  favor_count: number;
  grudge_count: number;
  manual_count: number;
  net_delta: number;
  recent_delta: number;
  evidence_level: string;
  last_event_age_days?: number | null;
  signals: string[];
  recent_reasons: Array<{
    type: string;
    description: string;
    source: string;
    delta: number;
    timestamp: number;
  }>;
};

const toast = useToastStore();
const loading = ref(false);
const error = ref("");
const actionResult = ref("");
const query = ref("");
const filter = ref<"all" | "warm" | "cold" | "locked">("all");
const states = ref<AffinityState[]>([]);
const selected = ref<{ state: AffinityState; events: AffinityEvent[]; relationship_explain?: RelationshipExplain } | null>(null);
const eventForm = reactive({ user_id: "", type: "favor", severity: 1, description: "" });
const adjustForm = reactive({ user_id: "", delta: 0, reason: "", manual_override: true });

const stateColumns = [
  { key: "user_id", label: "用户" },
  { key: "score", label: "分数" },
  { key: "level", label: "等级" },
  { key: "manual_override", label: "锁定" },
  { key: "last_event_at", label: "最近事件时间" },
  { key: "relationship_explain", label: "关系依据" },
  { key: "recent_events", label: "最近事件" },
  { key: "actions", label: "操作" }
];
const eventColumns = [
  { key: "type", label: "类型" },
  { key: "delta", label: "Delta" },
  { key: "severity", label: "强度" },
  { key: "source", label: "来源" },
  { key: "timestamp", label: "时间" },
  { key: "description", label: "描述" },
  { key: "actions", label: "操作" }
];
const selectedState = computed(() => selected.value?.state || null);
const selectedEvents = computed(() => selected.value?.events || []);
const selectedExplain = computed(() => selected.value?.relationship_explain || selected.value?.state.relationship_explain || null);
const warmLevels = ["friendly", "close", "trusted"];
const coldLevels = ["cold", "hostile"];
const warmCount = computed(() => states.value.filter((item) => warmLevels.includes(item.level)).length);
const coldCount = computed(() => states.value.filter((item) => coldLevels.includes(item.level)).length);
const lockedCount = computed(() => states.value.filter((item) => item.manual_override).length);
const thinEvidenceCount = computed(() => states.value.filter((item) => ["none", "sparse"].includes(item.relationship_explain?.evidence_level || "none")).length);
const canAddEvent = computed(() => Boolean(eventForm.user_id && eventForm.description.trim()));
const canAdjust = computed(() => Boolean(adjustForm.user_id && adjustForm.reason.trim()));
const filteredStates = computed(() => {
  const text = query.value.trim().toLowerCase();
  return states.value.filter((state) => {
    if (filter.value === "warm" && !warmLevels.includes(state.level)) return false;
    if (filter.value === "cold" && !coldLevels.includes(state.level)) return false;
    if (filter.value === "locked" && !state.manual_override) return false;
    if (!text) return true;
    return `${state.user_id} ${state.level} ${eventSummary(state.recent_events)} ${explainSummary(state.relationship_explain)}`.toLowerCase().includes(text);
  });
});

function formatScore(value: number) {
  const n = Number(value || 0);
  return `${n >= 0 ? "+" : ""}${n.toFixed(1)}`;
}
function levelStatus(level: string) {
  if (["close", "trusted", "friendly"].includes(level)) return "ok";
  if (["hostile", "cold"].includes(level)) return "error";
  return "pending";
}
function eventSummary(events?: AffinityEvent[]) {
  if (!events || !events.length) return "";
  return events.map((item) => `${typeLabel(item.type)} ${formatScore(item.delta)} ${item.description}`).join("\n");
}
function explainSummary(explain?: RelationshipExplain) {
  if (!explain) return "";
  const signals = signalSummary(explain.signals);
  return `${explain.event_count || 0} 事 / 净 ${formatScore(explain.net_delta || 0)}${signals ? ` / ${signals}` : ""}`;
}
function evidenceLabel(level?: string) {
  const labels: Record<string, string> = { none: "无依据", sparse: "依据少", steady: "依据稳定", rich: "依据充分" };
  return labels[level || ""] || "-";
}
function signalSummary(signals?: string[]) {
  const labels: Record<string, string> = {
    no_events: "无事件",
    manual_override: "手动锁定",
    stale: "较久未更新",
    mixed_history: "正负混合",
    strong_positive: "明显亲近",
    strong_negative: "明显冷淡"
  };
  return (signals || []).map((signal) => labels[signal] || signal).join(", ");
}
function typeLabel(type: string) {
  return type === "favor" ? "记恩" : type === "grudge" ? "记仇" : type;
}
function formatTime(value: number) {
  const n = Number(value || 0);
  return n ? new Date(n * 1000).toLocaleString() : "-";
}
function errorText(err: unknown, fallback: string) {
  return err instanceof Error ? err.message : fallback;
}
async function runAction(label: string, operation: () => Promise<unknown>) {
  loading.value = true;
  error.value = "";
  actionResult.value = "";
  try {
    await operation();
    actionResult.value = label;
    toast.push(label);
    await load(false);
  } catch (err) {
    error.value = errorText(err, `${label}失败`);
    toast.push(error.value, "error");
  } finally {
    loading.value = false;
  }
}
async function load(setBusy = true) {
  if (setBusy) loading.value = true;
  error.value = "";
  const selectedUserId = selected.value?.state.user_id;
  const previousSelected = selected.value;
  try {
    states.value = (await api<{ affinity: AffinityState[] }>("/api/affinity")).affinity || [];
    if (selectedUserId) {
      const detail = await api<{ state: AffinityState; events: AffinityEvent[]; relationship_explain?: RelationshipExplain }>(`/api/affinity/${selectedUserId}`);
      selected.value = detail;
    }
  } catch (err) {
    error.value = errorText(err, "好感度加载失败");
    selected.value = previousSelected;
  } finally {
    if (setBusy) loading.value = false;
  }
}
async function selectUser(userId: number) {
  loading.value = true;
  error.value = "";
  try {
    const detail = await api<{ state: AffinityState; events: AffinityEvent[]; relationship_explain?: RelationshipExplain }>(`/api/affinity/${userId}`);
    selected.value = detail;
    eventForm.user_id = String(userId);
    adjustForm.user_id = String(userId);
  } catch (err) {
    error.value = errorText(err, "关系详情加载失败");
  } finally {
    loading.value = false;
  }
}
async function addEvent() {
  if (!canAddEvent.value) return;
  await runAction("好感度事件已添加", async () => {
    await postJson("/api/affinity/event", { ...eventForm, user_id: Number(eventForm.user_id) });
    eventForm.description = "";
  });
}
async function adjust() {
  if (!canAdjust.value) return;
  await runAction("好感度已调整", async () => {
    await postJson("/api/affinity/adjust", { ...adjustForm, user_id: Number(adjustForm.user_id) });
    adjustForm.reason = "";
  });
}
async function deleteEvent(userId: number, eventId: number) {
  if (!window.confirm("确定删除这条好感度事件？")) return;
  await runAction("事件已删除", () => api(`/api/affinity/${userId}/events/${eventId}`, { method: "DELETE" }));
}
async function clearUser(userId: number) {
  if (!window.confirm("确定清除该用户的所有好感度记录？")) return;
  await runAction("好感度记录已清除", async () => {
    await api(`/api/affinity/${userId}`, { method: "DELETE" });
    if (selected.value?.state.user_id === userId) selected.value = null;
  });
}
onMounted(load);
</script>

<style scoped>
.save-result {
  border: 1px solid #386f47;
  background: #142016;
  color: var(--success);
  border-radius: 8px;
  padding: 10px 12px;
}

.explain-cell {
  display: grid;
  gap: 4px;
  min-width: 120px;
}

.explain-cell span {
  color: var(--muted);
  font-size: 12px;
}

.explain-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 10px;
  margin-bottom: 12px;
}

.explain-grid div {
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--panel-2);
  padding: 10px;
}

.explain-grid span {
  display: block;
  color: var(--muted);
  font-size: 12px;
}

.explain-grid strong {
  display: block;
  margin-top: 5px;
  font-size: 14px;
}
</style>
